import {TextureQuality} from "@app/common/components/viewers/configurator/extensions/bd-cloud/types"
import {ExtensionContext} from "@app/common/components/viewers/configurator/extensions/configurator-api-extension"
import {getOwnedNodes, SceneProperties, ScenePropertiesParameters, TemplateGraph} from "@cm/template-nodes"

const highTextureQuality = {textureResolution: "original", textureStreamingMode: "high-quality"} as const
//Unset values fall back to the scene property defaults
const standardTextureQuality = {textureResolution: undefined, textureStreamingMode: undefined}

/**
 * Scene properties for templates edited in the BD editor that do not define their own. Adding them must not change the rendering, so the texture
 * settings (Standard quality, no filtering) and subdivision limits match the defaults of a template without scene properties. The remaining values are
 * the same as when adding scene properties in the template editor.
 */
export const createBdSceneProperties = (): SceneProperties =>
    new SceneProperties({
        parameters: {
            //Without scene properties, subdivision is limited to 5 (the renderer's maximum) on desktop and 0 on mobile / AR
            maxSubdivisionLevel: 5,
            maxSubdivisionLevelOnMobile: 0,
            backgroundColor: [1, 1, 1],
            shiftAndZoomBackgroundImage: false,
            uiColor: [0, 0, 0],
            uiStyle: "icons",
            iconSize: 24,
            enableAr: false,
            enableSalesEnquiry: false,
            textureFiltering: false,
            enableRealtimeShadows: true,
            enableRealtimeLights: true,
            enableRealtimeMaterials: true,
            enableOnboardingHint: false,
            enableGltfDownload: false,
            enableStlDownload: false,
            enableSnapshot: true,
            enablePdfDownload: false,
            enableFullscreen: true,
            environmentMapMode: "full",
            showAnnotations: true,
            enableDimensionGuides: false,
            enableAdaptiveSubdivision: false,
            verticalArPlacement: false,
        },
    })

const getSceneProperties = (templateGraph: TemplateGraph): SceneProperties | undefined => getOwnedNodes(templateGraph, SceneProperties)[0]

/**
 * Updates the scene properties of the loaded template, templates without scene properties get the BD scene properties first. Like a viewer setting,
 * the change is saved with the template but not undoable.
 */
const updateSceneProperties = (context: ExtensionContext, values: Partial<ScenePropertiesParameters>): void => {
    const {sceneManagerService} = context
    //Nothing loaded yet, the scene manager starts with an empty graph
    if (!sceneManagerService.$templateRevisionId()) return

    //Setting a default on a template without scene properties changes nothing, the node is only added for an actual change
    if (!getSceneProperties(sceneManagerService.$templateGraph())) {
        const defaults: Partial<ScenePropertiesParameters> = createBdSceneProperties().parameters
        const changesDefaults = (Object.keys(values) as (keyof ScenePropertiesParameters)[]).some((key) => values[key] !== defaults[key])
        if (!changesDefaults) return
    }

    sceneManagerService.modifyTemplateGraph(
        (templateGraph) => {
            let sceneProperties = getSceneProperties(templateGraph)
            if (!sceneProperties) {
                sceneProperties = createBdSceneProperties()
                templateGraph.parameters.nodes.addEntry(sceneProperties)
            }
            sceneProperties.updateParameters(values)
        },
        true,
        false,
    )
}

/**
 * High only if both the resolution and the streaming mode match the high quality values, anything else (incl. templates without scene properties) is
 * standard.
 */
export const getTextureQuality = (templateGraph: TemplateGraph): TextureQuality => {
    const parameters = getSceneProperties(templateGraph)?.parameters
    const isHigh =
        parameters?.textureResolution === highTextureQuality.textureResolution && parameters.textureStreamingMode === highTextureQuality.textureStreamingMode
    return isHigh ? "High" : "Standard"
}

export const setTextureQuality = (context: ExtensionContext, quality: TextureQuality): void => {
    updateSceneProperties(context, quality === "High" ? highTextureQuality : standardTextureQuality)
}

/** Templates without scene properties render without texture filtering. */
export const getTextureFiltering = (templateGraph: TemplateGraph): boolean => getSceneProperties(templateGraph)?.parameters.textureFiltering ?? false

export const setTextureFiltering = (context: ExtensionContext, enabled: boolean): void => {
    updateSceneProperties(context, {textureFiltering: enabled})
}
